// v1.2 panel — the v1.1 double/halve panel PLUS a richer card for effect 9 (personality). Personality
// is just effect 9 on the same bus + feel.json as effects 1–8; it gets bespoke controls here only
// because it has more than one knob (rarity, a six-beat reel, flirt/troll) than a generic card holds.
// Not a separate tier — same juice, richer card.
//
// Reuses the whole v1.1 panel by mounting it, then appends the personality card to the same host
// — no duplication of the effect-card / double-halve machinery.

import { feel, DEFAULT_FEEL } from "../juice/feel";
import { PARAM_HINTS, roundToStep } from "./scale";
import { mountV1_1, numberControl, speedSlider } from "./v1_1";
import { setLivelyWords, fireOnce, replayLast } from "./fire";
import {
  setPersonalityBig,
  setReelSpeed,
  setPersonalityStyle,
  PERSONALITY_STYLES,
} from "../juice/effects/09-personality";

const ROWS: Array<[string, string]> = [
  ["intensity", "liveliness"],
  ["activation", "activate %"],
  ["emoteChance", "emote freq"],
  ["flirtChance", "flirt freq"],
  ["standMs", "reel 1·stand"],
  ["windupMs", "reel 2·windup"],
  ["travelMs", "reel 3·travel"],
  ["reactMs", "reel 4·react"],
  ["sitMs", "reel 5·sit"],
  ["resolveMs", "reel 6·resolve"],
  ["particleCount", "particles"],
  ["particleScale", "particle size"],
  ["reactDelayMs", "react delay"],
  ["holdMs", "hold ms"],
];

// Plain-language definitions shown on hover over each parameter label (these terms are opaque).
const P_GLOSSARY: Record<string, string> = {
  intensity: "Overall dial — how many tiles get character and how strongly they express.",
  activation: "What share of eligible letters actually 'wake up' with a colour skin / face.",
  emoteChance: "How often a rare tile flicks an eyebrow expression on a play.",
  flirtChance: "How often two epic tiles pair off into the flirt / troll reel.",
  standMs: "Reel beat 1 — how long the sender rises and holds before winding up.",
  windupMs: "Reel beat 2 — the anticipation jiggle just before it fires.",
  travelMs: "Reel beat 3 — how long the heart / troll particle takes to cross the gap.",
  reactMs: "Reel beat 4 — the pay-off pose the instant the particle lands.",
  sitMs: "Reel beat 5 — how long they hold that reaction before settling.",
  resolveMs: "Reel beat 6 — the settle back down to a resting face.",
  particleCount: "How many love / troll particles fire during a reel.",
  particleScale: "How big those reel particles are.",
  reactDelayMs: "The pause after the letters land before the reactions pop.",
  holdMs: "How long a single-tile emote holds before relaxing back to a resting face.",
};

/** Build a single buttony cycler rendered as `<Value>` (angle brackets are literal, signalling "click
 *  to cycle"). One click advances forward via `cycle()`; `label()` returns the new text. Optional
 *  `off()` marks the value inactive so it renders un-highlighted (e.g. the Vanilla state). */
function makeCycler(label: () => string, cycle: () => void, off?: () => boolean): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "t11-cycle";
  const paint = (): void => {
    btn.textContent = `<${label()}>`;
    btn.classList.toggle("is-off", !!off?.());
  };
  paint();
  btn.addEventListener("click", () => (cycle(), paint()));
  return btn;
}

/** Roll random personality params over their tuner ranges (mirrors v1.1's randomizeAll, personality-local). */
function randomizePersonality(cfg: Record<string, any>): void {
  for (const key of Object.keys(cfg)) {
    if (key === "enabled") continue;
    if (typeof cfg[key] === "number") {
      const hint = PARAM_HINTS[key] ?? { min: 0, max: 1 };
      cfg[key] = roundToStep(hint.min + Math.random() * (hint.max - hint.min), hint);
    }
  }
}

export function mountV1_2(host: HTMLElement, leftHost: HTMLElement): () => void {
  const disposeV11 = mountV1_1(host); // Juice → the RIGHT rail
  const cfg = feel.personality as Record<string, any>;

  const section = document.createElement("div");
  section.className = "tuner11 t11-personality";
  section.innerHTML = `
    <div class="t11-head">
      <span class="t11-title">Personality — Under The Hood</span>
      <label class="t11-master"><input type="checkbox" id="p-enabled"> on</label>
    </div>
    <div class="t11-hint">Rarity is fixed per letter — <b>Q Z X J</b> epic (flirt) · <b>K V B W Y F</b>
      rare (emote) · <b>C M P G D H</b> uncommon (colour). Liveliness scales how many activate + how
      often they express. Reactions pop <b>after</b> the letters land. Every change fires a word.</div>
    <div class="t11-global">
      <button id="p-new" data-tip="Fire a NEW lively word at the current replay speed">New word</button>
      <button id="p-replay" data-tip="Replay the current word at the current replay speed">Replay</button>
    </div>
    <div class="t11-global">
      <span class="t11-row-tag">Personality:</span>
      <button id="p-rand" class="t11-mini" data-tip="Roll random personality settings — activation, emote/flirt chance, reel timings, particles">Randomize</button>
      <button id="p-default" class="t11-mini" data-tip="Restore the smart launch defaults for personality (and reset global speed) — always a safe way back">Default</button>
    </div>
    <div class="t11-global">
      <span class="t11-row-tag">Buddy:</span>
      <span id="p-buddy-slot"></span>
    </div>
    <button class="t11-accordion" id="p-fine-toggle" aria-expanded="false"><span class="t11-acc-arrow">▾</span> Open Advanced Tuning Controls</button>
    <div class="t11-collapsible" id="p-fine-wrap" hidden>
      <div class="t11-gwrap" id="p-gwrap"></div>
      <div class="t11-global-sep"></div>
      <div class="p-controls"></div>
    </div>
  `;

  const syncers: Array<() => void> = [];
  const enabled = section.querySelector<HTMLInputElement>("#p-enabled")!;
  enabled.checked = cfg.enabled;
  enabled.addEventListener("change", () => (cfg.enabled = enabled.checked));

  // Reel global speed — a layered multiplier on the personality reel (independent of the JUICE rail),
  // now a standard slider at the top of Advanced Tuning. reelSpd is re-applied on every rail fire.
  let reelSpd = 1;
  section.querySelector("#p-new")!.addEventListener("click", () => (setReelSpeed(reelSpd), fireOnce()));
  section.querySelector("#p-replay")!.addEventListener("click", () => (setReelSpeed(reelSpd), replayLast()));

  // Buddy cycler — one `<Eyebrows>` button; click cycles the personality style (Eyebrows / Limbs / …).
  const STYLE_LABEL: Record<string, string> = { eyebrows: "Eyebrows", limbs: "Limbs" };
  let styleIdx = 0;
  const buddy = makeCycler(
    () => STYLE_LABEL[PERSONALITY_STYLES[styleIdx]] ?? PERSONALITY_STYLES[styleIdx],
    () => {
      styleIdx = (styleIdx + 1) % PERSONALITY_STYLES.length;
      setPersonalityStyle(PERSONALITY_STYLES[styleIdx]);
      setReelSpeed(reelSpd);
      fireOnce();
    },
  );
  buddy.setAttribute("data-tip", "Click to cycle the personality style the reel uses (Eyebrows / Limbs).");
  section.querySelector("#p-buddy-slot")!.appendChild(buddy);

  // ---- Advanced globals (top of the fine-tune list): word pool + global speed ----
  // Lively cycler — <Lively> / <Vanilla>. Lively biases toward longer, rarer words (effects are easy
  // to see while tuning); Vanilla is genuinely random (closer to real play). Off (Vanilla) renders grey.
  let liveOn = true; // default ON — matches setLivelyWords / bigMode defaults
  const lively = makeCycler(
    () => (liveOn ? "Lively" : "Vanilla"),
    () => {
      liveOn = !liveOn;
      setLivelyWords(liveOn);
      setPersonalityBig(liveOn);
      if (liveOn && !cfg.enabled) {
        cfg.enabled = true;
        enabled.checked = true;
      }
      setReelSpeed(reelSpd);
      fireOnce();
    },
    () => !liveOn,
  );
  lively.setAttribute(
    "data-tip",
    "Lively = deliberately longer, rarer words so effects are easy to see while tuning. " +
      "Vanilla = genuinely random words, closer to what average play looks like.",
  );
  const livelyRow = document.createElement("div");
  livelyRow.className = "t11-param t11-gcycle";
  const livelyLabel = document.createElement("label");
  livelyLabel.textContent = "word pool";
  livelyRow.append(livelyLabel, lively);

  const gspeed = speedSlider((m) => ((reelSpd = m), setReelSpeed(m)));
  const gwrap = section.querySelector<HTMLElement>("#p-gwrap")!;
  gwrap.appendChild(livelyRow);
  gwrap.appendChild(gspeed.el);

  // Default — restore the pristine launch personality feel + reset global speed. A safe way back.
  // (Mirrors Randomize's scope: params only, leaving the on/off master + view toggles alone.)
  section.querySelector("#p-default")!.addEventListener("click", () => {
    const def = (DEFAULT_FEEL as Record<string, any>).personality;
    for (const key of Object.keys(cfg)) {
      if (key === "enabled") continue;
      if (typeof cfg[key] === "number" || typeof cfg[key] === "string") cfg[key] = def[key];
    }
    gspeed.reset();
    syncers.forEach((s) => s());
    setReelSpeed(reelSpd);
    fireOnce();
  });

  // Randomize — personality-local (rolls this rail's params only).
  section.querySelector("#p-rand")!.addEventListener("click", () => {
    randomizePersonality(cfg);
    syncers.forEach((s) => s());
    setReelSpeed(reelSpd);
    fireOnce();
  });

  // fine-tune accordion (collapsed by default — only liveliness-and-below hide, so it's less busy).
  const fineToggle = section.querySelector<HTMLButtonElement>("#p-fine-toggle")!;
  const fineWrap = section.querySelector<HTMLElement>("#p-fine-wrap")!;
  const fineArrow = fineToggle.querySelector<HTMLElement>(".t11-acc-arrow")!;
  fineToggle.addEventListener("click", () => {
    fineWrap.hidden = !fineWrap.hidden;
    fineToggle.setAttribute("aria-expanded", String(!fineWrap.hidden));
    fineArrow.textContent = fineWrap.hidden ? "▾" : "▴";
  });

  const controls = section.querySelector<HTMLElement>(".p-controls")!;
  for (const [key, label] of ROWS) {
    const row = document.createElement("div");
    row.className = "t11-param";
    const l = document.createElement("label");
    l.textContent = label;
    if (P_GLOSSARY[key]) l.setAttribute("data-tip", P_GLOSSARY[key]);
    const ctrl = numberControl(cfg, key, PARAM_HINTS[key], syncers, { slider: true });
    row.append(l, ctrl.el);
    controls.appendChild(row);
  }

  leftHost.appendChild(section); // Personality → the LEFT rail (flanks the board opposite Juice)
  return () => {
    disposeV11();
    section.remove();
  };
}
